import { currentAccountId } from './account'
import { defaultPaper, type DueOn, type Paper, type Tier } from './paper'

/**
 * HOW THIS SHOP WORKS, SET ONCE.
 *
 * SET-02. Everything here is a policy of the business rather than a fact about
 * a house: the ladder it discounts on, when it expects the balance, and the
 * two paragraphs of terms beside the price. All three were hardcoded, so every
 * shop in the trade opened every quote on one company's months, one company's
 * percentages and one company's wording, and had to retype its own on all
 * forty houses in a season.
 *
 * THE SAFEGUARD IS THE POINT, and it is the one line of the spec worth more
 * than the screen: defaults apply to new drafts, never to historical
 * documents. A quote already written is a thing somebody was shown and may
 * have signed. Changing the shop's terms in November must not silently rewrite
 * what a homeowner agreed to in September, so nothing here ever touches a
 * saved document. It is read once, at the moment a job is created, on the way
 * out of the factory. See applyTo, and the test that walks a saved paper
 * through it and asserts it comes out unchanged.
 *
 * IT IS NOT A GATE. lib/quote.ts is explicit that a contractor should never
 * have to open a settings screen before he can write a price, and that stays
 * true: prices are typed on the quote and nothing here asks for one. This is
 * only the things somebody would otherwise retype word for word.
 *
 * Stored beside the account like the letterhead and the deposit policy, and
 * for the same reason. It never goes into a document, an export or a network
 * request.
 */

export interface ShopDefaults {
  /**
   * THE RUNGS A NEW QUOTE OPENS ON.
   *
   * Any months, any percentages, or none at all. Most shops in this trade
   * discount early bookings, but not all of them do and the ones that do not
   * run the same three months, which is exactly why this is a list a person
   * edits rather than a September-October-November constant.
   */
  tiers: Tier[]
  /** Whether the rungs print along the bottom of a new quote. */
  showLadder: boolean
  /** What the discount is called. Blank names it after the month. */
  discountLabel: string
  /** Completion, or a date the contractor names. See paper.DueOn. */
  dueOn: DueOn
  /** The two paragraphs beside the price, in the shop's own words. */
  termsLeft: string
  termsRight: string
}

/**
 * THE SHIPPED DEFAULTS ARE THE OLD HARDCODED ONES, EXACTLY.
 *
 * Not an improvement on them and not a blank form. A shop that never opens
 * this screen must get the quote it got yesterday, or this change is a silent
 * repricing of everybody's work dressed up as a feature.
 */
export function shippedDefaults(): ShopDefaults {
  const p = defaultPaper()
  return {
    tiers: p.tiers.map((t) => ({ ...t })),
    showLadder: p.showLadder,
    discountLabel: p.discountLabel,
    dueOn: p.dueOn,
    termsLeft: p.termsLeft,
    termsRight: p.termsRight,
  }
}

const keyFor = () => `lightsos.defaults.${currentAccountId()}`

/** A rung is a month in range and a percentage that is not a discount uphill. */
export function validTier(t: Tier): boolean {
  return (
    Number.isFinite(t.month) &&
    t.month >= 0 &&
    t.month <= 11 &&
    Number.isFinite(t.offPct) &&
    t.offPct >= 0 &&
    t.offPct <= 100
  )
}

/**
 * TWO RUNGS ON THE SAME MONTH IS NOT A LADDER.
 *
 * The spec asks for invalid tier combinations to be rejected. There is exactly
 * one combination that is genuinely invalid rather than unusual: the same
 * month appearing twice, because then the price of installing in October has
 * two answers and the quote picks whichever it reads first. A ladder that goes
 * up rather than down is unusual and somebody may mean it, so it is allowed
 * and said out loud on the screen instead.
 */
export function tierProblems(tiers: Tier[]): string[] {
  const out: string[] = []
  const seen = new Set<number>()
  for (const t of tiers) {
    if (!validTier(t)) {
      out.push('A rung needs a month and a percentage between 0 and 100.')
      continue
    }
    if (seen.has(t.month)) out.push('Two rungs cannot be set on the same month.')
    seen.add(t.month)
  }
  return [...new Set(out)]
}

/** Rising discounts as the season gets later. Allowed, and worth mentioning. */
export function ladderRunsUphill(tiers: Tier[]): boolean {
  const sorted = [...tiers].filter(validTier).sort((a, b) => a.month - b.month)
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].offPct > sorted[i - 1].offPct) return true
  }
  return false
}

/** Everything off disk, merged over the shipped set so a new field is never undefined. */
export function normalizeDefaults(raw: Partial<ShopDefaults> | undefined): ShopDefaults {
  const base = shippedDefaults()
  if (!raw) return base
  const tiers = Array.isArray(raw.tiers) ? raw.tiers.filter(validTier).map((t) => ({ ...t })) : null
  return {
    // An empty ladder is a real answer: this shop does not discount. Only a
    // missing one falls back.
    tiers: tiers ?? base.tiers,
    showLadder: typeof raw.showLadder === 'boolean' ? raw.showLadder : base.showLadder,
    discountLabel: typeof raw.discountLabel === 'string' ? raw.discountLabel : base.discountLabel,
    dueOn: raw.dueOn === 'date' || raw.dueOn === 'completion' ? raw.dueOn : base.dueOn,
    termsLeft: typeof raw.termsLeft === 'string' ? raw.termsLeft : base.termsLeft,
    termsRight: typeof raw.termsRight === 'string' ? raw.termsRight : base.termsRight,
  }
}

export function loadDefaults(): ShopDefaults {
  try {
    const raw = localStorage.getItem(keyFor())
    if (!raw) return shippedDefaults()
    return normalizeDefaults(JSON.parse(raw) as Partial<ShopDefaults>)
  } catch {
    return shippedDefaults()
  }
}

export function saveDefaults(d: ShopDefaults) {
  try {
    localStorage.setItem(keyFor(), JSON.stringify(d))
  } catch {
    /* private mode: it just does not persist */
  }
}

/**
 * A NEW DOCUMENT'S PAPER, AND ONLY A NEW ONE.
 *
 * Pure, and takes the paper it is starting from rather than reaching for one,
 * so the only way a saved quote can be touched by this is if a caller hands it
 * one. Nothing does. The caller is store/useEditor.ts, on the line that makes
 * a fresh document, beside the deposit policy that already worked this way.
 */
export function applyTo(paper: Paper, d: ShopDefaults): Paper {
  return {
    ...paper,
    tiers: d.tiers.map((t) => ({ ...t })),
    showLadder: d.showLadder,
    discountLabel: d.discountLabel,
    dueOn: d.dueOn,
    termsLeft: d.termsLeft,
    termsRight: d.termsRight,
  }
}

/**
 * What SET-02 asks for that nothing behind this screen can carry, said on the
 * screen rather than left as a blank section somebody hunts for.
 */
export const NOT_HERE: string[] = [
  'Who can do what. There is one login and no team, so a role grid would be a screen with one row on it.',
  'Notification preferences. Nothing in this app sends a message, so there is nothing to prefer.',
  'Message templates, for the same reason.',
  'Prices. They are typed on the quote on purpose: a rate card you have to fill in before you can write a price is a rate card nobody fills in.',
]
